import { Flex } from '@chakra-ui/react';
import { useIsNetchexNewNavAndAdminOrManagerPage } from 'Deer/hooks/useIsNetchexNewNavAndAdminOrManagerPage';
import { useSelectIsL2Visible } from 'Deer/slice/DeerNav.slice';
import { useToast } from 'components/hooks/useToast';
import { useDispatch } from 'react-redux';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { TOAST_STATUSES } from 'constants/ProjectConstants';
import {
  NETCHEX_NEW_ICON_ONLY_SIDEPANEL_WIDTH,
  NETCHEX_SIDEPANEL_WIDTH,
  isNetchexLogin,
} from 'constants/netChexConstants';

import { Button } from 'uiToolkitV1/Button';

import { setLabelSettings, setViewType, useCompetencySettings } from '../competencySettingsSlice';
import {
  useGetCompetencyLabelsQuery,
  useGetCompetencySettingsQuery,
  useUpdateCompetencyLabelsMutation,
  useUpdateCompetencySettingsMutation,
} from './dataHooks';
import { isScorePercentValidator } from './helpers/isScorePercentValidator';
import { Label } from './types/labels';

export const CompetencySettingsFooter = ({
  setShowSettingsScreen,
}: {
  setShowSettingsScreen?: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { data: competencySettings } = useGetCompetencySettingsQuery();
  const { data: competencyLabels } = useGetCompetencyLabelsQuery();
  const { viewType, labelSettings } = useCompetencySettings();
  const { mutate: updateCompetencySettings } = useUpdateCompetencySettingsMutation();
  const { mutate: updateCompetencyLabels } = useUpdateCompetencyLabelsMutation();
  const dispatch = useDispatch();
  const toast = useToast();

  const isPercentInvalid = isScorePercentValidator(labelSettings);

  const isSidebarVisible = useSelectIsL2Visible();

  const { isNetchexNewSidePanelCollapsed, isNetchexNewSideNav } = useIsNetchexNewNavAndAdminOrManagerPage();

  const hasCompetencySettingsChanged = competencySettings?.viewType !== viewType;
  const hasCompetencyLabelsChanged =
    competencyLabels?.answerOptions.length !== labelSettings.length ||
    competencyLabels?.answerOptions.some(
      (option, i) =>
        Number(option.score) !== Number(labelSettings[i]?.upperLimit) || option.description !== labelSettings[i]?.label
    );

  const saveCompetencySettings = () => {
    updateCompetencySettings(
      { viewType },
      {
        onSuccess: () => {
          setShowSettingsScreen?.(false);
          toast({
            title: 'Competency settings saved',
            status: TOAST_STATUSES.SUCCESS,
          });
        },
        onError: () => {
          toast({
            title: 'Failed to save competency settings! Please try again.',
            status: TOAST_STATUSES.ERROR,
          });
        },
      }
    );
  };

  const saveCompetencyLabels = () => {
    updateCompetencyLabels(
      {
        label: {
          ...competencyLabels,
          answerOptions: labelSettings.map((setting, i) => ({
            description: setting.label,
            score: setting.upperLimit,
            displayOrder: i + 1,
          })),
        },
      },
      {
        onSuccess: (data: Label) => {
          if (!checkNewNavEnabled()) {
            setShowSettingsScreen?.(false);
          }
          dispatch(
            setLabelSettings(
              data.answerOptions.map((option) => ({ upperLimit: option.score, label: option.description }))
            )
          );
        },
      }
    );
  };

  const onSave = () => {
    if (hasCompetencySettingsChanged) {
      saveCompetencySettings();
    }
    if (hasCompetencyLabelsChanged) {
      saveCompetencyLabels();
    }
  };

  const onCancel = () => {
    if (hasCompetencySettingsChanged) {
      dispatch(setViewType(competencySettings.viewType));
    }
    if (hasCompetencyLabelsChanged) {
      dispatch(
        setLabelSettings(
          competencyLabels.answerOptions.map((option) => ({ upperLimit: option.score, label: option.description }))
        )
      );
    }
    if (!checkNewNavEnabled()) {
      setShowSettingsScreen?.(false);
    }
  };

  let netchexNewNavWidth = '100%';
  if (isNetchexNewSideNav) {
    netchexNewNavWidth = isNetchexNewSidePanelCollapsed
      ? `calc(100% - ${NETCHEX_NEW_ICON_ONLY_SIDEPANEL_WIDTH})`
      : `calc(100% - ${NETCHEX_SIDEPANEL_WIDTH})`;
  }

  const getLeftPos = () => {
    if (isNetchexLogin) {
      return null;
    }
    if (checkNewNavEnabled()) {
      return isSidebarVisible ? '320px' : '80px';
    }
    return '19%';
  };

  const leftPos = getLeftPos();

  return (
    <Flex
      h="72px"
      backgroundColor="neutral.F5F7F9"
      p={4}
      borderTopWidth="1px"
      pos="fixed"
      left={leftPos}
      right={0}
      bottom={0}
      w={isNetchexLogin ? netchexNewNavWidth : '100%'}
    >
      <Button
        variant="tertiary"
        mr="auto"
        isDisabled={!hasCompetencyLabelsChanged && !hasCompetencySettingsChanged}
        onClick={onCancel}
      >
        Cancel
      </Button>
      <Button
        isDisabled={isPercentInvalid || (!hasCompetencyLabelsChanged && !hasCompetencySettingsChanged)}
        onClick={onSave}
      >
        Save
      </Button>
    </Flex>
  );
};
